import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";

export default function Funnel() {
  const { user } = useAuth();
  const { t } = useI18n();
  if (!user) return <div className="tb-loading">{t("common.loading")}</div>;
  if (user.role === "accelerator") return <AcceleratorPortfolio />;
  return <FunnelBoard kind="founder" headingTestId="funnel-heading" showPageChrome />;
}

function lastImportStorageKey(uid) {
  return `tb-accel-last-import:${uid}`;
}

function formatImportStamp(value, lang) {
  if (!value) return "";
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const parsed = new Date(dateOnly ? `${value}T12:00:00` : value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(lang === "fr" ? "fr-CA" : "en-CA", dateOnly
    ? { dateStyle: "medium" }
    : { dateStyle: "medium", timeStyle: "short" });
}

function AcceleratorPortfolio() {
  const { user, setUser } = useAuth();
  const { t, lang } = useI18n();
  const fileRef = useRef(null);
  const [importText, setImportText] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [importErr, setImportErr] = useState("");
  const [exportErr, setExportErr] = useState("");
  const [lastImport, setLastImport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [draftFounder, setDraftFounder] = useState("");
  const [draftAdvisor, setDraftAdvisor] = useState("");
  const [draftProgram, setDraftProgram] = useState("");
  const [draftCohort, setDraftCohort] = useState("");
  const [draftYear, setDraftYear] = useState("");
  const [founderFilter, setFounderFilter] = useState("");
  const [advisorFilter, setAdvisorFilter] = useState("");
  const [programFilter, setProgramFilter] = useState("");
  const [cohortFilter, setCohortFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [founders, setFounders] = useState([]);
  const [advisors, setAdvisors] = useState([]);

  useEffect(() => {
    api.get("/community").then((r) => {
      setFounders((r.data?.mine?.entrepreneur || []).filter((f) => !f.team_owner_id));
      setAdvisors(r.data?.mine?.advisor || []);
    }).catch(() => {});
  }, [reloadKey]);

  useEffect(() => {
    if (window.location.hash !== "#csv-import") return;
    const align = () => {
      const el = document.getElementById("csv-import");
      const page = document.querySelector(".tb-page");
      if (!el || !page) return;
      const bar = document.querySelector(".tb-app-bar");
      const topGap = (bar ? bar.getBoundingClientRect().height : 64) + 16;
      el.style.scrollMarginTop = `${topGap}px`;
      const shortfall = window.innerHeight - el.offsetHeight - topGap;
      const pad = shortfall > 80 ? `${Math.ceil(shortfall)}px` : "";
      if (page.style.paddingBottom !== pad) page.style.paddingBottom = pad;
      el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    align();
    const page = document.querySelector(".tb-page");
    const obs = page ? new ResizeObserver(align) : null;
    if (page && obs) obs.observe(page);
    const stop = window.setTimeout(() => obs?.disconnect(), 3000);
    return () => {
      obs?.disconnect();
      window.clearTimeout(stop);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    const saved = user.last_import;
    if (saved?.at) {
      setLastImport({
        n: saved.imported || 0,
        e: saved.errors || 0,
        file: saved.file || "",
        at: saved.at,
      });
      return;
    }
    try {
      const raw = localStorage.getItem(lastImportStorageKey(user.id));
      if (raw) setLastImport(JSON.parse(raw));
    } catch {
      /* noop */
    }
  }, [user?.id, user?.last_import?.at]);

  const downloadTemplate = async () => {
    const token = localStorage.getItem("tb_token");
    const r = await fetch(`${API_BASE}/accelerator/import-template`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "accelerator-import-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const dismissImport = async () => {
    setLastImport(null);
    setImportErr("");
    try {
      if (user?.id) localStorage.removeItem(lastImportStorageKey(user.id));
    } catch {
      /* noop */
    }
    try {
      await api.delete("/accelerator/last-import");
      setUser((prev) => (prev && typeof prev === "object" ? { ...prev, last_import: null } : prev));
    } catch {
      /* the banner is already hidden */
    }
  };

  const downloadExport = async () => {
    setExportErr("");
    const token = localStorage.getItem("tb_token");
    const r = await fetch(`${API_BASE}/accelerator/export`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      setExportErr(t("funnel.exportFailed"));
      return;
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "accelerator-founders-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const canImport = !user?.team_owner_id;

  const runImport = async (e) => {
    e.preventDefault();
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setBusy(true); setImportErr("");
    try {
      const { data } = await api.post("/accelerator/import", { csv_text: importText, filename: importFileName || "" });
      const n = (data.imported || []).length;
      const errs = data.errors || [];
      const stamp = data.last_import || {};
      const rec = {
        n: stamp.imported ?? n,
        e: stamp.errors ?? errs.length,
        file: stamp.file || importFileName || "",
        at: stamp.at || new Date().toISOString(),
      };
      try {
        if (user?.id) localStorage.setItem(lastImportStorageKey(user.id), JSON.stringify(rec));
      } catch {
        /* noop */
      }
      setLastImport(rec);
      setImportText("");
      setImportFileName("");
      if (fileRef.current) fileRef.current.value = "";
      if (errs.length) setImportErr(errs.map((x) => `row ${x.row || "?"}: ${x.error}`).join(" · "));
      setReloadKey((k) => k + 1);
    } catch (err) {
      setImportErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const onFile = (file) => {
    if (!file) return;
    setImportFileName(file.name || "");
    const reader = new FileReader();
    reader.onload = () => setImportText(String(reader.result || ""));
    reader.readAsText(file);
  };

  const people = [...founders, ...advisors];
  const programs = [...new Set(people.map((p) => p.program).filter(Boolean))];
  const cohorts = [...new Set(people.map((p) => p.cohort).filter(Boolean))];
  const years = [...new Set(people.map((p) => p.year).filter(Boolean))];

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="funnel-heading">{t("portfolio.title")}</h1>
      <p className="tb-muted" style={{ marginTop: 0, marginBottom: 16, maxWidth: 760 }}>{t("portfolio.subtitleAccelerator")}</p>

      <div className="tb-inline-filters" data-testid="funnel-accel-filters" style={{ marginBottom: 32, alignItems: "center" }}>
        <select value={draftFounder} onChange={(e) => setDraftFounder(e.target.value)} data-testid="funnel-filter-founder">
          <option value="">{t("dash.filterAllFounders")}</option>
          {founders.map((f) => (
            <option key={f.id} value={f.id}>{f.name}{f.company ? ` · ${f.company}` : ""}</option>
          ))}
        </select>
        <select value={draftAdvisor} onChange={(e) => setDraftAdvisor(e.target.value)} data-testid="funnel-filter-advisor">
          <option value="">{t("dash.filterAllAdvisors")}</option>
          {advisors.map((a) => (
            <option key={a.id} value={a.id}>{a.name}{a.company ? ` · ${a.company}` : ""}</option>
          ))}
        </select>
        <select value={draftProgram} onChange={(e) => setDraftProgram(e.target.value)} data-testid="funnel-filter-program">
          <option value="">{t("dash.filterAllPrograms")}</option>
          {programs.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={draftCohort} onChange={(e) => setDraftCohort(e.target.value)} data-testid="funnel-filter-cohort">
          <option value="">{t("dash.filterAllCohorts")}</option>
          {cohorts.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={draftYear} onChange={(e) => setDraftYear(e.target.value)} data-testid="funnel-filter-year">
          <option value="">{t("dash.filterAllYears")}</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button
          type="button"
          className="tb-primary-btn"
          data-testid="funnel-filter-apply"
          onClick={() => {
            setFounderFilter(draftFounder);
            setAdvisorFilter(draftAdvisor);
            setProgramFilter(draftProgram);
            setCohortFilter(draftCohort);
            setYearFilter(draftYear);
          }}
        >
          {t("funnel.filterApply")}
        </button>
      </div>

      <FunnelBoard
        key={`f-${reloadKey}`}
        kind="founder"
        heading={t("funnel.foundersBoard")}
        headingTestId="founder-funnel-heading"
        founderId={founderFilter}
        program={programFilter}
        cohort={cohortFilter}
        year={yearFilter}
        showPageChrome={false}
        connectionRole="entrepreneur"
      />

      <FunnelBoard
        key={`a-${reloadKey}`}
        kind="advisor"
        heading={t("funnel.advisorsBoard")}
        headingTestId="advisor-funnel-heading"
        founderId={advisorFilter}
        program={programFilter}
        cohort={cohortFilter}
        year={yearFilter}
        showPageChrome={false}
        connectionRole="advisor"
        style={{ marginTop: 32 }}
      />

      <section id="csv-import" data-testid="data-management" style={{ scrollMarginTop: 96, marginTop: 32 }}>
      <h2 className="tb-h2" style={{ marginTop: 0 }} data-testid="data-management-heading">{t("funnel.dataManagement")}</h2>
      {canImport ? (
      <form onSubmit={runImport} className="tb-block" data-testid="csv-import-form">
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>{t("funnel.importTitle")}</h3>
        <input ref={fileRef} type="file" accept=".csv,text/csv" disabled={busy} onChange={(e) => onFile(e.target.files?.[0])} data-testid="csv-import-file" />
        <textarea rows={4} value={importText} disabled={busy} onChange={(e) => setImportText(e.target.value)} placeholder={t("funnel.importPlaceholder")} data-testid="csv-import-text" style={{ marginTop: 8, caretColor: busy ? "transparent" : "auto" }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
          <button type="submit" className="tb-primary-btn" disabled={busy || !importText.trim()} data-testid="csv-import-submit">
            {busy ? t("auth.loading") : t("funnel.importSubmit")}
          </button>
          <button type="button" className="tb-ghost-btn" onClick={downloadTemplate} data-testid="csv-template-download">{t("funnel.downloadTemplate")}</button>
        </div>
        <div style={{ marginTop: 16 }}>
          {lastImport && (
            <p className="tb-muted" data-testid="csv-import-result" style={{ margin: 0, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span>
                {t("funnel.importResult", {
                  n: lastImport.n,
                  e: lastImport.e,
                  file: lastImport.file || t("funnel.importPasted"),
                  when: formatImportStamp(lastImport.at || lastImport.date, lang),
                })}
              </span>
              <button type="button" className="tb-ghost-btn" onClick={dismissImport} data-testid="csv-import-dismiss" style={{ fontSize: "0.78rem", padding: "4px 10px" }}>
                {t("funnel.dismissImport")}
              </button>
            </p>
          )}
          {importErr && <div className="tb-form-error" data-testid="csv-import-errors">{importErr}</div>}
        </div>
      </form>
      ) : (
        <div className="tb-block" data-testid="csv-import-principal-only">
          <p className="tb-muted" style={{ margin: 0 }}>{t("funnel.importPrincipalOnly")}</p>
        </div>
      )}
      <div className="tb-block" style={{ marginTop: 16 }} data-testid="csv-export-section">
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>{t("funnel.exportTitle")}</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="data-management-downloads">
          <button type="button" className="tb-ghost-btn" onClick={downloadExport} data-testid="csv-export-download">{t("funnel.downloadExport")}</button>
        </div>
        {exportErr && <div className="tb-form-error" data-testid="csv-export-error" style={{ marginTop: 10 }}>{exportErr}</div>}
      </div>
      </section>
    </div>
  );
}

function csvCell(value) {
  const text = String(value || "").trim();
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function FunnelBoard({ kind = "founder", founderId = "", program = "", cohort = "", year = "", showPageChrome = true, heading, headingTestId, connectionRole = "entrepreneur", style }) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(false);
  const [stages, setStages] = useState([]);
  const [picker, setPicker] = useState(null);
  const [manual, setManual] = useState({ name: "", email: "", company: "" });
  const [manualErr, setManualErr] = useState("");
  const [manualBusy, setManualBusy] = useState(false);
  const canAddPerson = user?.role === "accelerator" && !user?.team_owner_id;
  const [myConnections, setMyConnections] = useState([]);

  const load = async () => {
    setErr("");
    try {
      const { data: next } = await api.get("/funnel", { params: { funnel_kind: kind } });
      setData(next);
      setStages(next.funnel.stages);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const loadConnections = async () => {
    try {
      const { data: comm } = await api.get("/community");
      setMyConnections(comm.mine?.[connectionRole] || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); loadConnections(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) {
    return (
      <div className={showPageChrome ? "tb-page" : ""} style={style}>
        {!showPageChrome && heading && (
          <h2 className="tb-h2" style={{ margin: "0 0 8px" }} data-testid={headingTestId}>{heading}</h2>
        )}
        {err ? <div className="tb-form-error" data-testid={`funnel-load-error-${kind}`}>{err}</div> : <div className="tb-loading">{t("common.loading")}</div>}
      </div>
    );
  }

  const saveStages = async () => {
    try {
      await api.put("/funnel", { stages, kind });
      setEditing(false);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addManual = async (e) => {
    e.preventDefault();
    setManualBusy(true);
    setManualErr("");
    try {
      const role = kind === "advisor" ? "advisor" : "entrepreneur";
      const header = "full_name,email,role,principal_colleague,company";
      const line = [manual.name, manual.email, role, "principal", manual.company].map(csvCell).join(",");
      await api.post("/accelerator/import", { csv_text: `${header}\n${line}`, filename: "one-person" });
      setManual({ name: "", email: "", company: "" });
      setPicker(null);
      load();
    } catch (err) {
      setManualErr(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setManualBusy(false);
    }
  };

  const addEntry = async (stage_key, person_id) => {
    try {
      await api.post("/funnel/entries", { founder_id: person_id, person_id, stage_key, kind });
      setPicker(null);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const moveEntry = async (eid, stage_key) => {
    await api.put(`/funnel/entries/${eid}`, { stage_key });
    load();
  };

  const updateNotes = async (eid, notes) => {
    await api.put(`/funnel/entries/${eid}`, { notes });
  };

  const updateMeta = async (eid, patch) => {
    await api.put(`/funnel/entries/${eid}`, patch);
    load();
  };

  const removeEntry = async (eid) => {
    if (!window.confirm(t("funnel.confirmRemove"))) return;
    await api.delete(`/funnel/entries/${eid}`);
    load();
  };

  const visibleEntries = data.entries.filter((e) => {
    const person = e.person || e.founder || {};
    const pid = e.person_id || e.founder_id;
    if (founderId && pid !== founderId) return false;
    if (program && (person.program || "") !== program) return false;
    if (cohort && (e.cohort || person.cohort || "") !== cohort) return false;
    if (year && (e.year || person.year || "") !== year) return false;
    return true;
  });
  const entriesByStage = (key) => visibleEntries.filter((e) => e.stage_key === key);
  const usedIds = new Set(data.entries.map((e) => e.person_id || e.founder_id));
  const usedCompanies = new Set(
    data.entries
      .map((e) => ((e.person || e.founder || {}).company || "").trim().toLowerCase())
      .filter(Boolean)
  );
  const available = myConnections.filter((c) => {
    if (usedIds.has(c.id)) return false;
    if (kind === "founder") {
      if (c.team_owner_id) return false;
      const co = (c.company || "").trim().toLowerCase();
      if (co && usedCompanies.has(co)) return false;
    }
    return true;
  });
  const stageList = data.funnel.stages;
  const addLabel = kind === "advisor" ? t("funnel.addAdvisor") : (t("funnel.addCompany") || t("funnel.addFounder"));
  const pickLabel = kind === "advisor" ? t("funnel.pickAdvisor") : (t("funnel.pickCompany") || t("funnel.pickFounder"));

  return (
    <div className={showPageChrome ? "tb-page" : ""} style={style}>
      {showPageChrome && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: "0.4rem" }}>
            <h1 className="tb-h1" style={{ margin: 0 }} data-testid={headingTestId || "funnel-heading"}>{t("portfolio.title")}</h1>
            <div style={{ display: "flex", gap: 8 }}>
              {!editing ? (
                <button className="tb-ghost-btn" onClick={() => setEditing(true)} data-testid={`funnel-edit-stages-${kind}`}>
                  {t("funnel.editStages")}
                </button>
              ) : (
                <>
                  <button className="tb-ghost-btn" onClick={() => { setStages(data.funnel.stages); setEditing(false); }}>
                    {t("book.cancel")}
                  </button>
                  <button className="tb-primary-btn" onClick={saveStages} data-testid={`funnel-save-stages-${kind}`}>
                    {t("funnel.saveStages")}
                  </button>
                </>
              )}
            </div>
          </div>
          <p className="tb-muted" style={{ marginTop: 0, marginBottom: "1.5rem", maxWidth: 760 }}>{t("portfolio.subtitleFunnel")}</p>
        </>
      )}
      {!showPageChrome && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
          {heading ? (
            <h2 className="tb-h2" style={{ margin: 0 }} data-testid={headingTestId}>{heading}</h2>
          ) : <span />}
          <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
            {!editing ? (
              <button className="tb-ghost-btn" onClick={() => setEditing(true)} data-testid={`funnel-edit-stages-${kind}`}>
                {t("funnel.editStages")}
              </button>
            ) : (
              <>
                <button className="tb-ghost-btn" onClick={() => { setStages(data.funnel.stages); setEditing(false); }}>{t("book.cancel")}</button>
                <button className="tb-primary-btn" onClick={saveStages} data-testid={`funnel-save-stages-${kind}`}>{t("funnel.saveStages")}</button>
              </>
            )}
          </div>
        </div>
      )}
      {err && <div className="tb-form-error" style={{ marginBottom: 16 }}>{err}</div>}

      {editing && (
        <div className="tb-block" style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            {stages.map((s, i) => (
              <div key={i} className="tb-block" style={{ background: "var(--surface)" }}>
                <label style={{ fontSize: "0.72rem" }}>EN</label>
                <input type="text" value={s.label_en} onChange={(e) => setStages(stages.map((x, j) => j === i ? { ...x, label_en: e.target.value } : x))} data-testid={`stage-edit-en-${kind}-${i}`} />
                <label style={{ fontSize: "0.72rem", marginTop: 8 }}>FR</label>
                <input type="text" value={s.label_fr} onChange={(e) => setStages(stages.map((x, j) => j === i ? { ...x, label_fr: e.target.value } : x))} data-testid={`stage-edit-fr-${kind}-${i}`} />
                {stages.length > 1 && (
                  <button className="tb-ghost-btn" style={{ marginTop: 8, fontSize: "0.78rem" }} onClick={() => setStages(stages.filter((_, j) => j !== i))} data-testid={`stage-remove-${kind}-${i}`}>
                    {t("funnel.removeStage")}
                  </button>
                )}
              </div>
            ))}
          </div>
          <button className="tb-ghost-btn" style={{ marginTop: 12 }} onClick={() => setStages([...stages, { key: "", label_en: "New stage", label_fr: "Nouvelle étape" }])} data-testid={`stage-add-${kind}`}>
            + {t("funnel.addStage")}
          </button>
        </div>
      )}

      <div className="tb-funnel-grid" data-testid={kind === "founder" ? "funnel-board" : `funnel-board-${kind}`}>
        {stageList.map((stage) => {
          const entries = entriesByStage(stage.key);
          const label = lang === "fr" ? (stage.label_fr || stage.label_en) : stage.label_en;
          return (
            <div key={stage.key} className="tb-funnel-col" data-testid={`stage-col-${kind}-${stage.key}`}>
              <div className="tb-funnel-col-head">
                <span>{label}</span>
                <small className="tb-muted">{entries.length}</small>
              </div>
              <div className="tb-funnel-col-body">
                {entries.length === 0 && (
                  <p className="tb-muted" style={{ fontSize: "0.78rem", padding: "8px 0" }}>{t("funnel.empty")}</p>
                )}
                {kind === "founder"
                  ? groupEntriesByCompany(entries).map((g) => (
                    <CompanyFunnelCard
                      key={g.key}
                      group={g}
                      stageList={stageList}
                      lang={lang}
                      t={t}
                      onMove={moveEntry}
                      onNotes={updateNotes}
                      onMeta={updateMeta}
                      onRemove={removeEntry}
                    />
                  ))
                  : entries.map((e) => (
                    <FunnelCard key={e.id} entry={e} stageList={stageList} lang={lang} t={t} onMove={moveEntry} onNotes={updateNotes} onMeta={updateMeta} onRemove={removeEntry} kind={kind} />
                  ))}
                {picker === stage.key ? (
                  <div className="tb-funnel-card" style={{ background: "var(--surface)" }}>
                    {available.length === 0 ? (
                      <p className="tb-muted" style={{ fontSize: "0.78rem", margin: 0 }}>
                        {myConnections.length === 0 ? t("funnel.noConnections") : t("funnel.allInFunnel")}
                      </p>
                    ) : (
                      <>
                        <label style={{ fontSize: "0.72rem" }}>{pickLabel}</label>
                        <select onChange={(ev) => ev.target.value && addEntry(stage.key, ev.target.value)} defaultValue="" data-testid={`picker-${kind}-${stage.key}`}>
                          <option value="">—</option>
                          {available.map((f) => (
                            <option key={f.id} value={f.id}>{f.name}{f.company ? ` · ${f.company}` : ""}</option>
                          ))}
                        </select>
                      </>
                    )}
                    {canAddPerson && (
                      <form onSubmit={addManual} data-testid={`manual-add-${kind}`} style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        <label style={{ fontSize: "0.72rem" }}>{t("funnel.manualAdd")}</label>
                        <input required value={manual.name} onChange={(ev) => setManual((m) => ({ ...m, name: ev.target.value }))} placeholder={t("funnel.manualName")} data-testid={`manual-name-${kind}`} />
                        <input required type="email" value={manual.email} onChange={(ev) => setManual((m) => ({ ...m, email: ev.target.value }))} placeholder={t("funnel.manualEmail")} data-testid={`manual-email-${kind}`} />
                        <input value={manual.company} onChange={(ev) => setManual((m) => ({ ...m, company: ev.target.value }))} placeholder={t("funnel.manualCompany")} data-testid={`manual-company-${kind}`} />
                        {manualErr && <div className="tb-form-error">{manualErr}</div>}
                        <button type="submit" className="tb-primary-btn" disabled={manualBusy} data-testid={`manual-submit-${kind}`}>
                          {manualBusy ? t("auth.loading") : t("funnel.manualSubmit")}
                        </button>
                      </form>
                    )}
                    <button className="tb-ghost-btn" style={{ marginTop: 6, fontSize: "0.78rem" }} onClick={() => setPicker(null)}>{t("book.cancel")}</button>
                  </div>
                ) : (
                  <button className="tb-ghost-btn" style={{ width: "100%", fontSize: "0.82rem" }} onClick={() => setPicker(stage.key)} data-testid={`add-to-${kind}-${stage.key}`}>
                    + {addLabel}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {available.length > 0 && (
        <p className="tb-muted" style={{ marginTop: "1rem", fontSize: "0.82rem" }}>
          {t("funnel.unassignedHint")}{" "}
          <Link to="/app/community" style={{ color: "var(--accent)" }}>{t("nav.community")}</Link>
        </p>
      )}
    </div>
  );
}

function HealthFlags({ value, onChange, t, testId }) {
  const levels = [
    { key: "green", label: t("funnel.healthGreen") },
    { key: "yellow", label: t("funnel.healthYellow") },
    { key: "orange", label: t("funnel.healthOrange") },
    { key: "red", label: t("funnel.healthRed") },
  ];
  const [open, setOpen] = useState(false);
  const current = levels.find((l) => l.key === value);

  if (!open) {
    return (
      <button
        type="button"
        className="tb-health-chip"
        onClick={() => setOpen(true)}
        data-testid={testId}
        aria-label={t("funnel.health")}
      >
        {current ? (
          <>
            <span className={`tb-health-flag ${current.key} on`} aria-hidden="true" />
            <span>{current.label}</span>
          </>
        ) : (
          <span className="tb-muted">{t("funnel.healthSet")}</span>
        )}
      </button>
    );
  }

  return (
    <div className="tb-health-flags" role="group" aria-label={t("funnel.health")} data-testid={`${testId}-picker`}>
      {levels.map((l) => (
        <button
          key={l.key}
          type="button"
          className={`tb-health-option${value === l.key ? " on" : ""}`}
          aria-pressed={value === l.key}
          data-testid={`${testId}-${l.key}`}
          onClick={() => {
            onChange(l.key);
            setOpen(false);
          }}
        >
          <span className={`tb-health-flag ${l.key} on`} aria-hidden="true" />
          <span>{l.label}</span>
        </button>
      ))}
    </div>
  );
}

function companyGroupKey(e) {
  const p = e.person || e.founder || {};
  return p.team_owner_id || p.company || p.id || e.id;
}

function groupEntriesByCompany(entries) {
  const map = new Map();
  for (const e of entries) {
    const p = e.person || e.founder || {};
    const key = `${companyGroupKey(e)}::${e.stage_key}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        stage_key: e.stage_key,
        company: p.company || p.name,
        principalId: p.team_owner_id || p.id,
        entries: [],
      });
    }
    map.get(key).entries.push(e);
  }
  return [...map.values()];
}

function CompanyFunnelCard({ group, stageList, lang, t, onMove, onNotes, onMeta, onRemove }) {
  const first = group.entries.find((e) => !(e.person || e.founder || {}).team_owner_id) || group.entries[0];
  const person = first.person || first.founder || {};
  const [health, setHealth] = useState(first.health || "");
  useEffect(() => { setHealth(first.health || ""); }, [first.id, first.health]);
  const moveAll = (stage) => Promise.all(group.entries.map((e) => onMove(e.id, stage)));
  const archiveAll = () => Promise.all(group.entries.map((e) => onMeta(e.id, { archived: true })));
  const removeAll = () => {
    if (!window.confirm(t("funnel.confirmRemove"))) return;
    return Promise.all(group.entries.map((e) => onRemove(e.id)));
  };
  const setCompanyHealth = (next) => {
    setHealth(next);
    onMeta(first.id, { health: next });
  };
  return (
    <div className="tb-funnel-card" data-testid={`entry-${first.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/app/u/${person.id || group.principalId}`} style={{ color: "var(--text)", textDecoration: "none", fontWeight: 600, fontSize: "0.88rem" }} data-testid={`company-link-${group.principalId}`}>
            {group.company}
          </Link>
          {person.name && person.name !== group.company && (
            <div className="tb-muted" style={{ fontSize: "0.74rem" }}>{person.name}</div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 4, fontSize: "0.74rem" }}>
            <Link to={`/app/u/${person.id || group.principalId}?tab=metrics`}>{t("portfolio.title")}</Link>
            <Link to={`/app/u/${person.id || group.principalId}?tab=mandates`}>{t("nav.mandates")}</Link>
          </div>
        </div>
        <button className="tb-funnel-del" onClick={removeAll} aria-label="remove" data-testid={`remove-${first.id}`}>×</button>
      </div>
      <HealthFlags value={health} onChange={setCompanyHealth} t={t} testId={`health-${first.id}`} />
      <select
        value={group.stage_key}
        onChange={(e) => moveAll(e.target.value)}
        style={{ marginTop: 8, width: "100%", fontSize: "0.78rem" }}
        data-testid={`move-${first.id}`}
      >
        {stageList.map((s) => (
          <option key={s.key} value={s.key}>
            {lang === "fr" ? (s.label_fr || s.label_en) : s.label_en}
          </option>
        ))}
      </select>
      <button className="tb-ghost-btn" style={{ marginTop: 6, fontSize: "0.74rem", padding: "4px 8px" }} onClick={archiveAll} data-testid={`archive-${first.id}`}>
        {t("funnel.archive")}
      </button>
    </div>
  );
}

function FunnelCard({ entry, stageList, lang, t, onMove, onNotes, onMeta, onRemove }) {
  const [notes, setNotes] = useState(entry.notes || "");
  const [expanded, setExpanded] = useState(false);
  const f = entry.person || entry.founder;
  if (!f) return null;
  return (
    <div className="tb-funnel-card" data-testid={`entry-${entry.id}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/app/u/${f.id}`} style={{ color: "var(--text)", textDecoration: "none", fontWeight: 600, fontSize: "0.88rem" }}>
            {f.name}
          </Link>
          {f.company && <div className="tb-muted" style={{ fontSize: "0.74rem" }}>{f.company}</div>}
        </div>
        <button className="tb-funnel-del" onClick={() => onRemove(entry.id)} aria-label="remove" data-testid={`remove-${entry.id}`}>×</button>
      </div>
      <select
        value={entry.stage_key}
        onChange={(e) => onMove(entry.id, e.target.value)}
        style={{ marginTop: 8, width: "100%", fontSize: "0.78rem" }}
        data-testid={`move-${entry.id}`}
      >
        {stageList.map((s) => (
          <option key={s.key} value={s.key}>
            {lang === "fr" ? (s.label_fr || s.label_en) : s.label_en}
          </option>
        ))}
      </select>
      <button className="tb-ghost-btn" style={{ marginTop: 6, fontSize: "0.74rem", padding: "4px 8px" }} onClick={() => onMeta(entry.id, { archived: true })} data-testid={`archive-${entry.id}`}>
        {t("funnel.archive")}
      </button>
      {!expanded ? (
        <button className="tb-ghost-btn" style={{ marginTop: 6, fontSize: "0.74rem", padding: "4px 8px" }} onClick={() => setExpanded(true)}>
          {entry.notes ? t("funnel.editNotes") : t("funnel.addNotes")}
        </button>
      ) : (
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => onNotes(entry.id, notes)}
          rows={3}
          style={{ marginTop: 6, width: "100%", fontSize: "0.8rem" }}
          placeholder={t("funnel.notesPlaceholder")}
          data-testid={`notes-${entry.id}`}
        />
      )}
    </div>
  );
}
